import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '@/components/chat/chat-types';
import type { UniversalChatContextPayload } from '@/components/chat/universal-chat-context';
import { createSummaryMetadata } from '@/components/summarizer/summary-metadata';
import type { SummaryImage } from '@/lib/media/summary-images';
import type { Tweet as TweetType } from '@/types/tweet';
import type { InlineTweetReference, StreamState } from '../types';
import { createExplainContextPayload, createExplainMessages, truncateText } from '../utils';
import type { SummaryBaseState } from './useSummarySettings';
import { useTweetStreaming } from './useTweetStreaming';
export interface UseExplainRequestParameters {
  appendMessages: (messages: Message[]) => void;
  includeImagesInPrompt: boolean;
  inlineTweetUrls: InlineTweetReference[];
  isChatEnabled: boolean;
  openChat: () => void;
  registerContext: (context: UniversalChatContextPayload) => void;
  settings: SummaryBaseState;
  summaryImages: SummaryImage[];
  tweet: TweetType;
}
export interface UseExplainRequestResult {
  explain: () => Promise<void>;
  explanationState: StreamState;
  isExplaining: boolean;
}
export function useExplainRequest({
  tweet,
  summaryImages,
  inlineTweetUrls,
  includeImagesInPrompt,
  settings,
  isChatEnabled,
  openChat,
  registerContext,
  appendMessages
}: UseExplainRequestParameters): UseExplainRequestResult {
  const [explanationState, setExplanationState] = useState<StreamState>(() => ({
    status: 'idle',
    text: '',
    error: null,
    metadata: createSummaryMetadata({
      availableImages: summaryImages.length,
      shouldIncludeImages: includeImagesInPrompt,
      mode: 'explain'
    })
  }));
  const [isExplainPending, setIsExplainPending] = useState(false);
  const explainInFlightReference = useRef(false);
  const explanationAbortReference = useRef<AbortController | null>(null);
  const isMountedReference = useRef(true);
  const startTweetStream = useTweetStreaming(isMountedReference);
  useEffect(() => {
    isMountedReference.current = true;
    const abortReference = explanationAbortReference;
    return () => {
      isMountedReference.current = false;
      abortReference.current?.abort();
    };
  }, []);
  const explain = useCallback(async () => {
    if (explainInFlightReference.current) {
      return;
    }
    explainInFlightReference.current = true;
    if (!isChatEnabled) {
      const requestBody = {
        summaryLength: 'xl',
        model: settings.summaryModel,
        includeImages: includeImagesInPrompt,
        mode: 'explain' as const,
        searchMode: settings.summarySearchEnabled ? 'auto' : 'off'
      };
      const initialMetadata = createSummaryMetadata({
        summaryLength: 'xl',
        requestedModel: settings.summaryModel,
        mode: 'explain',
        searchMode: settings.summarySearchEnabled ? 'auto' : 'off',
        requestedSearchMode: settings.summarySearchEnabled ? 'auto' : 'off',
        shouldIncludeImages: includeImagesInPrompt,
        availableImages: summaryImages.length
      });
      try {
        await startTweetStream({
          endpoint: `/api/tweets/${tweet.id}/summarize`,
          requestBody,
          abortRef: explanationAbortReference,
          setState: setExplanationState,
          errorMessage: 'Unable to explain tweet.',
          initialMetadata
        });
      } finally {
        explainInFlightReference.current = false;
      }
      return;
    }
    setIsExplainPending(true);
    try {
      openChat();
      const rawText = tweet.noteTweet?.text?.trim() || tweet.text?.trim() || '';
      const summaryPreview = rawText.length > 0 ? truncateText(rawText, 400) : null;
      const contextPayload = createExplainContextPayload({
        tweet,
        inlineTweetUrls,
        summaryImages,
        summaryPreview
      });
      registerContext(contextPayload);
      const timestamp = new Date();
      const [systemMessage, userMessage] = createExplainMessages(tweet.id, timestamp);
      appendMessages([systemMessage, userMessage]);
    } finally {
      setIsExplainPending(false);
      explainInFlightReference.current = false;
    }
  }, [appendMessages, includeImagesInPrompt, inlineTweetUrls, isChatEnabled, openChat, registerContext, settings.summaryModel, settings.summarySearchEnabled, startTweetStream, summaryImages, tweet]);
  const isExplaining = isExplainPending || explanationState.status === 'loading' || explanationState.status === 'streaming';
  return {
    explanationState,
    isExplaining,
    explain
  };
}